import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";

// ✅ IMPORTANT: Extend dayjs with customParseFormat
dayjs.extend(customParseFormat);

const Bar1 = () => {
  const [data, setData] = useState([]);
  const [walletData, setWalletData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [graphFilter, setGraphFilter] = useState("last2months");
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  const COLORS = {
    revenue: "#FF5B5B",
    PurchaseValue: "#9CA3AF",
    Sales: "#383D63",
    walletTopUp: "#34D399",
  };

  /* ================= FETCH REPORT DATA ================= */
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const token = localStorage.getItem("token");
        const partnerCode = localStorage.getItem("partnerCode");

        const fromDate = dayjs().subtract(60, "day").format("MM/DD/YYYY");
        const toDate = dayjs().format("MM/DD/YYYY");

        const fromDateISO = dayjs().subtract(60, "day").format("YYYY-MM-DD");
        const toDateISO = dayjs().format("YYYY-MM-DD");

        // Fetch sales data
        const res = await fetch(
          `${apiUrl}ESIM/Get_purchasereport_sourcewise?flag=3&fromdate=${fromDate}&todate=${toDate}&partnercode=${partnerCode}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const json = await res.json();

        if (json.isSuccess && json.partnersale) {
          setData(json.partnersale);
        }

        // Fetch wallet top-up data
        const walletTopUpRes = await axios.post(
          `${apiUrl}Renew/GetNewWallet`,
          {
            pageIndex: 1,
            pageSize: 200,
            fromDate: fromDateISO,
            toDate: toDateISO,
            partnerCode: partnerCode,
            creditOrRefund: 1,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const walletResponse = walletTopUpRes.data;

        // ✅ FIX #1: Use correct property name "getwallet"
        if (walletResponse.isSuccess && walletResponse.getwallet) {
          console.log("✅ Wallet data fetched:", walletResponse.getwallet.length, "records");
          setWalletData(walletResponse.getwallet);
        } else {
          console.log("❌ Wallet API response:", walletResponse);
        }
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [apiUrl]);

  /* ================= HELPER: Parse Amount ================= */
  const parseAmount = (amountStr) => {
    if (!amountStr) return 0;
    // Remove "$", spaces, and any other non-numeric characters except decimal
    const cleaned = String(amountStr).replace(/[^0-9.-]/g, "").trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  /* ================= HELPER: Parse Date ================= */
  const parseDate = (dateStr) => {
    if (!dateStr) return null;

    // Try multiple formats
    const formats = [
      "MM/DD/YYYY HH:mm:ss",
      "MM/DD/YYYY",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
    ];

    for (const format of formats) {
      const parsed = dayjs(dateStr, format, true); // strict parsing
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // Fallback to native parsing
    const fallback = dayjs(dateStr);
    return fallback.isValid() ? fallback : null;
  };

  /* ================= BUILD MONTHLY GRAPH ================= */
  useEffect(() => {
    // Process even if one dataset exists
    if (!data.length && !walletData.length) return;

    console.log("📊 Building graph with:", data.length, "sales,", walletData.length, "wallet records");

    const startDate = dayjs().subtract(2, "month").startOf("month");

    // ================= GROUP SALES DATA =================
    const monthlyData = {};

    data.forEach((item) => {
      const date = parseDate(item.purchasedate);
      if (!date || date.isBefore(startDate)) return;

      const monthKey = date.format("YYYY-MM");
      const monthLabel = date.format("MMM YYYY");

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthLabel,
          sortKey: monthKey,
          revenue: 0,
          cost: 0,
          orders: 0,
          walletTopUp: 0,
        };
      }

      monthlyData[monthKey].revenue += Number(item.selling_price) || 0;
      monthlyData[monthKey].cost += Number(item.purchaseamount) || 0;
      monthlyData[monthKey].orders += 1;
    });

    // ================= GROUP WALLET DATA =================
    console.log("💳 Processing wallet data...");

    walletData.forEach((item, index) => {
      // ✅ FIX #2: Correct amount parsing
      const amount = parseAmount(item.amount);
      const date = parseDate(item.date);

      console.log(`  [${index}] Date: ${item.date} → Parsed: ${date?.format("YYYY-MM-DD") || "INVALID"}, Amount: "${item.amount}" → ${amount}`);

      if (!date) {
        console.log(`    ⚠️ Skipped: Invalid date`);
        return;
      }

      // ✅ FIX #3: More flexible date filtering
      if (date.isBefore(startDate)) {
        console.log(`    ⚠️ Skipped: Before start date (${startDate.format("YYYY-MM-DD")})`);
        return;
      }

      const monthKey = date.format("YYYY-MM");
      const monthLabel = date.format("MMM YYYY");

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthLabel,
          sortKey: monthKey,
          revenue: 0,
          cost: 0,
          orders: 0,
          walletTopUp: 0,
        };
      }

      monthlyData[monthKey].walletTopUp += amount;
      console.log(`    ✅ Added to ${monthKey}: $${amount}, Total: $${monthlyData[monthKey].walletTopUp}`);
    });

    // ================= FINALIZE DATA =================
    const finalData = Object.values(monthlyData)
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .map(({ sortKey, ...rest }) => rest);

    console.log("📈 Final graph data:", finalData);

    setGraphData(finalData);
  }, [data, walletData, graphFilter]);

  /* ================= TOOLTIP ================= */
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const d = payload[0].payload;

    return (
      <div className="bg-white p-3 border rounded shadow text-sm">
        <h4 className="font-bold mb-2 text-gray-800">{label}</h4>
        <p className="text-gray-600">💵 Purchase Value: ${d.cost?.toFixed(2) || "0.00"}</p>
        <p className="text-gray-600">📦 Sales: {d.orders || 0}</p>
        <p className="text-green-600 font-semibold">💳 Wallet Top-Up: ${d.walletTopUp?.toFixed(2) || "0.00"}</p>
      </div>
    );
  };

  /* ================= UI ================= */
  return (
    <div className="p-6 bg-white w-full rounded-xl shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Sales Dashboard</h2>

        <div className="flex items-center gap-4">
          {/* Debug info */}
          {/* <span className="text-xs text-gray-400">
            Sales: {data.length} | Wallet: {walletData.length}
          </span> */}

          <select
            value={graphFilter}
            onChange={(e) => setGraphFilter(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="last2months">Last 2 Months</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="h-[300px] flex justify-center items-center">
          <span className="text-gray-500">Loading...</span>
        </div>
      ) : graphData.length ? (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={graphData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" tickFormatter={(v) => `$${v}`} />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            <Bar
              yAxisId="left"
              dataKey="cost"
              fill={COLORS.PurchaseValue}
              name="Purchase Value"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              yAxisId="right"
              dataKey="orders"
              fill={COLORS.Sales}
              name="Sales"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              yAxisId="left"
              dataKey="walletTopUp"
              fill={COLORS.walletTopUp}
              name="Wallet Top-Up"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[300px] flex flex-col justify-center items-center text-gray-500">
          <p>No Data Available</p>
          <p className="text-xs mt-2">Check console for debug info</p>
        </div>
      )}
    </div>
  );
};

export default Bar1;